export type EditorFieldSource = "ai" | "context" | "manual" | "derived";
export type EditorFieldBadgeTone = "accent" | "neutral" | "outline";
export type EditorFieldGuardrailIntent = "heroHeadline" | "heroSubheadline";
export type EditorFieldSeoSignal = "critical" | "supporting";

export interface EditorFieldHydrationPolicy {
  overridePolicy?: "userWins" | "systemWins";
}

export interface EditorFieldGuardrails {
  intent?: EditorFieldGuardrailIntent;
  minLength?: number;
  maxLength?: number;
  requireLocation?: boolean;
  requireService?: boolean;
  disallowSubstrings?: string[];
  genericPhrases?: string[];
  maxWarnings?: number;
}

export interface EditorFieldMetadata {
  source?: EditorFieldSource;
  description?: string;
  seo?: boolean;
  promptGuidance?: string[];
  hydration?: EditorFieldHydrationPolicy;
  guardrails?: EditorFieldGuardrails;
}

export interface EditorFieldBadge {
  label: string;
  tone: EditorFieldBadgeTone;
}

export interface EditorFieldGuidance {
  badges: EditorFieldBadge[];
  description?: string;
  reassurance?: string;
  tips: string[];
}

export interface EditorFieldSeoIndicator {
  signal: EditorFieldSeoSignal;
  label: string;
  description: string;
}

function normalizeText(value?: string): string | undefined {
  if (typeof value !== "string") return undefined;

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function normalizeTips(values?: string[]): string[] {
  if (!Array.isArray(values)) return [];

  return values
    .map((value) => normalizeText(value))
    .filter((value): value is string => Boolean(value))
    .slice(0, 2);
}

export function getEditorFieldGuidance(metadata?: EditorFieldMetadata): EditorFieldGuidance {
  const source = metadata?.source;
  const description = normalizeText(metadata?.description);
  const tips = normalizeTips(metadata?.promptGuidance);
  const badges: EditorFieldBadge[] = [];

  if (source === "ai") {
    badges.push({ label: "AI draft", tone: "accent" });
  }

  if (source === "context") {
    badges.push({ label: "Business info", tone: "neutral" });
  }

  if (source === "derived") {
    badges.push({ label: "Auto-filled", tone: "neutral" });
  }

  if (metadata?.seo) {
    badges.push({ label: "SEO support", tone: "outline" });
  }

  let reassurance: string | undefined;

  switch (source) {
    case "ai":
      reassurance = "AI draft. Edit freely.";
      break;
    case "context":
      reassurance = "From your business info. Review for accuracy.";
      break;
    case "derived":
      reassurance = "Usually handled for you. Change only if needed.";
      break;
    default:
      if (metadata?.seo) {
        reassurance = "Write naturally.";
      }
      break;
  }

  if (metadata?.hydration?.overridePolicy === "userWins") {
    reassurance = reassurance ? `${reassurance} Your edits stay.` : "Your edits stay.";
  }

  return {
    badges,
    description,
    reassurance,
    tips,
  };
}

export function getEditorFieldSeoIndicator(
  metadata?: EditorFieldMetadata,
): EditorFieldSeoIndicator | null {
  if (metadata?.guardrails) {
    return {
      signal: "critical",
      label: "SEO-sensitive",
      description:
        "This field has stronger SEO impact. Edit freely, but keep the main service and location natural when they fit.",
    };
  }

  if (metadata?.seo) {
    return {
      signal: "supporting",
      label: "Supports SEO",
      description:
        "This field supports the page's search relevance, but it does not need the same guardrails as your primary headline.",
    };
  }

  return null;
}
